<script setup lang="ts">
    import Contact from '~/app/contact/models/Contact';
    import { ModelType } from '~/app/base/schemas/BaseSchema';
    import EndpointFactory from '~/app/factories/EndpointFactory';
    import InvoiceEndpoint from '~/app/invoicing/endpoints/InvoiceEndpoint';
    import Invoice from '~/app/invoicing/models/Invoice';
    import InvoiceSchema from '~/app/invoicing/schemas/InvoiceSchema';

    interface PayloadInterface {
        invoice: Invoice;
    }

    const currentInvoice = ref<Invoice | null>(null);
    const endpoint = EndpointFactory.make(ModelType.INVOICES) as InvoiceEndpoint;

    const form = useForm<{
        contact: Contact | null;
    }>({
        contact: null,
    });

    const { modalName } = useModal(
        'invoicing:invoice:add-contact-modal',
        (payload: PayloadInterface) => {
            currentInvoice.value = payload.invoice;
            form.set('contact', payload.invoice.contact);
        },
        () => {
            currentInvoice.value = null;
            form.reset();
        }
    );

    const submit = async () => {
        if (!currentInvoice.value) return;

        const schema = new InvoiceSchema({ id: currentInvoice.value.getId() });
        schema.addToOneRelationship('contact', ModelType.CONTACTS, form.get('contact').getId());

        const response = await form.loadUntil(endpoint.setInclude('contact,worksite').update(schema));

        if (response.error) {
            if (!response.validationErrors) {
                useToasteoError();
            }
            return;
        }

        useToasteoSuccess();
        close();
    };

    const close = () => {
        useEvent('modal:close', modalName);
    };
</script>

<template>
    <UiResponsiveModal
        :name="modalName"
        :width="500"
        :title="$t('selects.contact.title')"
    >
        <div :class="$theme('modal.padding')">
            <div class="mt-4">
                <h2 :class="$theme('modal.titleLeft.text')">{{ $t('selects.contact.title') }}</h2>
            </div>

            <form
                class="mt-2"
                @submit.prevent="submit"
            >
                <FormContactSelect
                    :form="form"
                    input-name="contact"
                    :label="$t('form.labels.contact')"
                    contact-type="client"
                    without-label
                    with-create-button
                />
                <UiAction
                    :content="{
                        label: 'actions.save',
                        icon: 'fa-regular fa-check',
                        type: 'main',
                    }"
                    modifier="full"
                    :is-loading="form.isLoading"
                    @click="submit"
                />
            </form>
        </div>
    </UiResponsiveModal>
</template>
